import { Users, Mail, Database, Globe, BarChart3, MessagesSquare, Bot } from "lucide-react";
import Reveal from "@/components/Reveal";
import { SectionLabel } from "@/components/common";
import { useLanguage } from "@/context/LanguageContext";

const systemIcons = [Users, Mail, Database, Globe, BarChart3, MessagesSquare];

export default function AutomationHub() {
  const { t } = useLanguage();
  const systems = t("home.hubSystems");
  const nodes = systems.map((label, i) => {
    const angle = -Math.PI / 2 + (i * Math.PI * 2) / systems.length;
    return { label, Icon: systemIcons[i], x: 50 + 38 * Math.cos(angle), y: 50 + 38 * Math.sin(angle) };
  });

  return (
    <section className="py-24 border-t border-white/8" data-testid="automation-hub-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <SectionLabel>{t("home.hubLabel")}</SectionLabel>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-xl">
            {t("home.hubH2")}
          </h2>
          <p className="mt-5 max-w-xl text-base text-slate-300 leading-relaxed">{t("home.hubSub")}</p>
          <div className="mt-8 flex flex-wrap gap-2">
            {systems.map((s) => (
              <span key={s} className="text-xs rounded-full border border-[#4F8CFF]/25 bg-[#4F8CFF]/8 px-3 py-1.5 text-slate-200">{s}</span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative mx-auto w-full max-w-[520px] aspect-square" data-testid="automation-hub-visual">
            <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" aria-hidden>
              {nodes.map((n, i) => (
                <g key={n.label}>
                  <line x1="50" y1="50" x2={n.x} y2={n.y} stroke="rgba(79,140,255,0.22)" strokeWidth="0.35" className="hub-line" />
                  <circle r="0.9" fill="#bfd8ff">
                    <animateMotion dur={`${2.6 + i * 0.35}s`} begin={`${i * 0.4}s`} repeatCount="indefinite" path={`M 50 50 L ${n.x} ${n.y}`} />
                  </circle>
                  <circle r="0.6" fill="#7C5CFF" opacity="0.9">
                    <animateMotion dur={`${3.1 + i * 0.3}s`} begin={`${i * 0.55 + 1.2}s`} repeatCount="indefinite" path={`M ${n.x} ${n.y} L 50 50`} />
                  </circle>
                </g>
              ))}
            </svg>

            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2">
              <div className="hub-core flex h-24 w-24 sm:h-28 sm:w-28 items-center justify-center rounded-full border border-[#4F8CFF]/50 bg-gradient-to-br from-[#0c1a33] to-[#07111F]">
                <Bot className="h-9 w-9 text-[#8fb4ff]" />
              </div>
              <span className="text-xs font-mono uppercase tracking-[0.18em] text-[#8fb4ff]">{t("home.hubCenter")}</span>
            </div>

            {nodes.map((n) => (
              <div
                key={n.label}
                className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5"
                style={{ left: `${n.x}%`, top: `${n.y}%` }}
              >
                <div className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-2xl border border-white/12 bg-[#0a1424]/80 backdrop-blur-md text-[#8fb4ff] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.8)]">
                  <n.Icon className="h-5 w-5" />
                </div>
                <span className="text-[11px] text-slate-300 whitespace-nowrap">{n.label}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
